import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  CorePinterestBoardFieldsFragment,
  ImportSourceArgsGroupInput,
  PinterestSearchContentType,
  PinterestSearchSortType,
  PinterestSearchType,
  PlanImporterType,
  SocialProfileFields_PinterestSocialProfile_Fragment
} from '../../../../models/graphql';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { Select } from '../../../../components/forms/Select';
import {
  SelectDropdownTemplate,
  SelectItem,
  SelectItemTemplate,
  SelectViewProps
} from '../../../../components/forms/Select/Select.view';
import { SelectProfileItemTemplate } from '../SelectTemplate';
import { SelectProfileTemplateViewProps } from '../SelectTemplate/SelectTemplate.view';
import { ContentFilterSubFilterWrapper } from '../ContentFilter';
import { SelectAutoComplete } from '../../../../components/forms/SelectAutoComplete';
import { SourceLevelAction } from '../SourceLevelAction';
import { FindContentContext, SearchStepEnum } from '../FindContentProvider';
import classNames from 'classnames';
import classes from '../ContentFilter/ContentFilter.module.scss';
import templateClasses from '../SelectTemplate/SelectTemplate.module.scss';
import { FormElementValueType } from '../../../../models/basic-objects';
import { ListItemContainer } from '../../../../components/containers/ListItemContainer';
import { ListItem } from '../../../../components/actions/ListItem';
import { ReactComponent as DefaultImage } from 'assets/images/defaultProfileImage.svg';
import { useDidMount } from '../../../../hooks/useDidMount';
import { SearchErrorBox } from '../SearchErrorBox';
import { Button } from '../../../../components/actions/Button';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { Input } from '../../../../components/forms/Input';
import { useDimensions } from '../../../../hooks/useDimensions';
import { BreakpointsValuesEnum } from '../../../../constants/breakpoints-values';
import { useOrganizationPlan } from 'hooks/useOrganizationPlan';
import { AdvancedFeature } from 'components/containers/AdvancedFeature';
import { AdvancedFeatures } from 'components/containers/AdvancedFeature/AdvancedFeature.view';

export type PinterestFilterBarProps = {
  formInstance: UseFormReturn;
  filterSearchHandler: (resetFilterBar?: boolean) => void;
  width: number;
  isBoardFilter: boolean;
};

export type ContentFilterPinterestViewProps = {
  formInstance: UseFormReturn;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  selectedAutoCompleteValue: FormElementValueType;
  setSelectedAutoCompleteValue: (value: FormElementValueType) => void;
  pinterestProfiles: SocialProfileFields_PinterestSocialProfile_Fragment[];
  pinterestBoards: CorePinterestBoardFieldsFragment[];
  submitFormHandler: (type: PinterestSearchType | PinterestTypesEnum, value: string) => void;
  filterSearchHandler: (resetFilterBar?: boolean) => void;
  hasPosts: boolean;
  showSaveSourceModal: boolean;
  setShowSaveSourceModal: (show: boolean) => void;
  getPinterestInputArgs: () => ImportSourceArgsGroupInput;
  apolloErrorKey: string | undefined;
  resetCallBackHandler: CallableFunction;
};

export enum PinterestTypesEnum {
  PROFILE_BOARD = 'PROFILE_BOARD',
  MY_PINS = 'MY_PINS',
  MY_BOARD = 'MY_BOARD'
}

export const PinterestFilterBar = (props: PinterestFilterBarProps): JSX.Element => {
  const { formInstance, width, isBoardFilter } = props;
  const { loading } = useContext(FindContentContext);
  const { importType, isTrial } = useOrganizationPlan();
  const isDisabledFeature = importType !== PlanImporterType.ADVANCED_IMPORTER;
  const filterBarContainerClasses = classNames('grid-x', classes.FilterBarContainer);

  // In place of setting default values, we determine if no value is set and do so
  // Setting default values will break loading a saved source
  useDidMount(() => {
    const { contentType, sortType } = formInstance.getValues();
    if (!contentType) {
      formInstance.setValue('contentType', PinterestSearchContentType.ALL);
    }
    if (!sortType) {
      formInstance.setValue('sortType', PinterestSearchSortType.LATEST);
    }
  });

  const filterDefaultWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-6': width < BreakpointsValuesEnum.large,
    'small-3': isBoardFilter && width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperQuadElement]: width >= BreakpointsValuesEnum.large
  });
  const filterInputWrapperClasses = classNames('cell', classes.InputWrapper, classes.HasButtonSibling, {
    'auto margin-top-small-8': width < BreakpointsValuesEnum.large,
    auto: width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperSmallFilterWidth]: !isBoardFilter && width >= BreakpointsValuesEnum.large,
    [classes.InputWrapperPnBoardsFilterWidth]: isBoardFilter && width >= BreakpointsValuesEnum.large
  });
  const filterButtonWrapperClasses = classNames('cell', classes.FilterButtonContainer, {
    'shrink margin-top-small-8': width < BreakpointsValuesEnum.large
  });

  const quadElementSubFilterContainerClasses = classNames('cell', 'small-6', classes.QuadElementSubFilterContainer);

  const finalFormElements = (
    <>
      <div className={filterInputWrapperClasses}>
        <Input
          formInstance={formInstance}
          name={'term'}
          placeholder={'Search within these posts'}
          rules={{}}
          size={'medium'}
          disabled={loading || isDisabledFeature}
          onKeyDown={e => {
            // ENTER key presses submits form
            if (e.key === 'Enter') {
              props.filterSearchHandler();
            }
          }}
        />
      </div>
      <div className={filterButtonWrapperClasses}>
        <Button
          type={'button'}
          variant={'hollow'}
          size={'medium'}
          color={ButtonColorsEnum.primary}
          disabled={loading || isDisabledFeature}
          expanded={true}
          action={() => props.filterSearchHandler()}>
          Apply
        </Button>
      </div>
      {(isTrial || isDisabledFeature) && (
        <div className={classNames('cell', classes.AdvancedFeatureContainer)}>
          <AdvancedFeature feature={AdvancedFeatures.filterFeed} />
        </div>
      )}
    </>
  );

  return (
    <div className={filterBarContainerClasses}>
      <div className={filterDefaultWrapperClasses}>
        <Select
          items={[
            { value: PinterestSearchContentType.ALL, label: 'All Content' },
            { value: PinterestSearchContentType.LINKS, label: 'All Links' }
          ]}
          name={'contentType'}
          size={'medium'}
          rules={{}}
          useFormInstance={formInstance}
          disabled={loading || isDisabledFeature}
        />
      </div>
      <div className={filterDefaultWrapperClasses}>
        <Select
          items={[{ value: PinterestSearchSortType.LATEST, label: 'Latest' }]}
          name={'sortType'}
          size={'medium'}
          rules={{}}
          useFormInstance={formInstance}
          disabled={loading || isDisabledFeature}
        />
      </div>
      {isBoardFilter && width >= BreakpointsValuesEnum.large ? (
        <div className={quadElementSubFilterContainerClasses}>
          <div className={'grid-x'}>{finalFormElements}</div>
        </div>
      ) : (
        <>{finalFormElements}</>
      )}
    </div>
  );
};

export const ContentFilterPinterestView = (props: ContentFilterPinterestViewProps): JSX.Element => {
  const {
    formInstance,
    showFilters,
    pinterestProfiles,
    submitFormHandler,
    selectedAutoCompleteValue,
    setSelectedAutoCompleteValue,
    filterSearchHandler,
    showSaveSourceModal,
    setShowSaveSourceModal,
    getPinterestInputArgs,
    apolloErrorKey,
    hasPosts,
    resetCallBackHandler
  } = props;
  const [filter, setFilter] = useState<string>('');
  const { loading, error, searchStep } = useContext(FindContentContext);

  const bodyRef = useRef<HTMLElement>(document.body);
  const { width } = useDimensions(bodyRef);

  const watchProfileBoard = formInstance.watch(PinterestTypesEnum.PROFILE_BOARD);
  const watchBoardsAutoComplete = formInstance.watch('boardsAutoComplete');
  const typeValue = useWatch({ control: formInstance.control, name: 'type' });

  // Several objects use a profiles boards, so we get them here
  const currentBoards = useMemo(() => {
    return pinterestProfiles.find(p => p.id === watchProfileBoard)?.boards || [];
  }, [pinterestProfiles, watchProfileBoard]);

  const pinterestProfileListItems = useMemo(() => {
    if (pinterestProfiles.length < 1) {
      return [{ label: 'You do not have Pinterest profiles', value: -1 }];
    } else {
      return pinterestProfiles.map(p => ({ label: p.displayName, value: p.id }));
    }
  }, [pinterestProfiles]);

  const profileItemTemplate: SelectItemTemplate = (props: SelectViewProps, index: number, item: SelectItem) => {
    const selectProps: SelectProfileTemplateViewProps = {
      ...props,
      index,
      item,
      showDisplayName: true,
      iconSize: 'small',
      socialProfiles: pinterestProfiles
    };

    return <SelectProfileItemTemplate key={index} {...selectProps} />;
  };

  const typeListItems = useMemo(() => {
    const own = [
      'Own Profiles',
      { label: 'My Pins', value: PinterestSearchType.PROFILE },
      { label: 'My Boards', value: PinterestSearchType.BOARD }
    ];

    const noProfiles: SelectItem[] = [{ label: 'No profile connected', value: -1 }];

    return pinterestProfiles.length > 0 ? [...own] : noProfiles;
  }, [pinterestProfiles]);

  const defaultSearchWrapperClasses = classNames('cell', classes.InputWrapper, {
    'small-6': width < BreakpointsValuesEnum.large,
    'small-3': typeValue === PinterestSearchType.BOARD,
    [classes.InputWrapperQuadElement]: typeValue === PinterestSearchType.BOARD
  });

  const thirdSearchWrapperClasses = classNames(
    'cell',
    classes.InputWrapper,
    width >= BreakpointsValuesEnum.large ? 'auto' : 'small-12',
    {
      [classes.InputWrapperDefaultMaxWidth]: width >= BreakpointsValuesEnum.large
    }
  );

  // We special case the boards wrapper class because its unique
  const profileBoardsWrapperClasses = classNames(defaultSearchWrapperClasses, {
    [classes.HasPaddingRight]: !watchProfileBoard
  });

  // We always want to ingore using right-margin
  const boardsWrapperClasses = classNames(defaultSearchWrapperClasses, classes.InputWrapperIgnoreMargin);

  const pinterestBoardsSelectItems = useMemo((): SelectItem[] => {
    if (currentBoards.length === 0) {
      return [{ label: 'No Boards', value: -1 }];
    }

    const boards = !!filter
      ? currentBoards.filter(b => b.name.toLowerCase().includes(filter.toLowerCase()))
      : currentBoards;

    return boards.map(b => ({ label: b.name, value: b.externalId }));
  }, [currentBoards, filter]);

  const dropdownTemplate: SelectDropdownTemplate = useCallback(
    args => {
      const { expanded, items, setExpanded } = args;

      return (
        <>
          {items.length === 0 ? (
            <ListItemContainer type={'div'} expanded={expanded}>
              <ListItem action={() => false} disabled={true} selected={false} type={'div'} variation={'regular'}>
                <div className={templateClasses.NoItemsFound}>No search result found</div>
              </ListItem>
            </ListItemContainer>
          ) : (
            <ListItemContainer type={'ul'} expanded={expanded || !selectedAutoCompleteValue}>
              {items.flatMap((item, index) => {
                if (typeof item === 'string') return [];

                const isSelected = selectedAutoCompleteValue === item.value;
                const board = currentBoards.find(b => b.externalId === item.value);

                const boardImageWrapper = classNames('cell grow', classes.LinkedInBoardImageWrapper);
                const boardLabelWrapper = classNames('cell auto', classes.LinkedInBoardLabelWrapper);

                if (!board) return [];

                return (
                  <ListItem
                    key={index}
                    role={'option'}
                    aria-label={''}
                    variation={'regular'}
                    type={'li'}
                    selected={isSelected}
                    action={() => {
                      const value = String(item.value);
                      const selectedItem = items.find(i => {
                        return typeof i !== 'string' && i.value === value;
                      });

                      const label =
                        selectedItem && typeof selectedItem !== 'string' ? selectedItem.label : selectedItem;

                      formInstance.setValue('boardsAutoComplete', label);
                      setSelectedAutoCompleteValue(value);
                      submitFormHandler(PinterestTypesEnum.MY_BOARD, value);

                      setExpanded(false);
                    }}
                    disabled={false}>
                    <div className={'grid-x align-middle'}>
                      <div className={boardImageWrapper}>
                        {board.photoUrl ? <img src={board.photoUrl} alt={'Board'} /> : <DefaultImage />}
                      </div>
                      <div className={boardLabelWrapper}>{item.label}</div>
                    </div>
                  </ListItem>
                );
              })}
            </ListItemContainer>
          )}
        </>
      );
    },
    [currentBoards, formInstance, selectedAutoCompleteValue, setSelectedAutoCompleteValue, submitFormHandler]
  );

  // Resetting the form does not trigger a value change,
  // so we make sure the watch and the filter are in-sync
  useEffect(() => {
    if (watchBoardsAutoComplete !== filter) {
      setFilter(watchBoardsAutoComplete);
    }
  }, [filter, watchBoardsAutoComplete]);

  return (
    <>
      <div className={defaultSearchWrapperClasses}>
        <Select
          label={'Search Type'}
          items={typeListItems}
          name={'type'}
          size={'medium'}
          rules={{}}
          useFormInstance={formInstance}
          disabled={loading || pinterestProfiles.length < 1}
          selectedValue={pinterestProfiles.length > 0 ? PinterestSearchType.PROFILE : -1}
        />
      </div>
      {typeValue === PinterestSearchType.PROFILE && (
        <div className={thirdSearchWrapperClasses}>
          <Select
            items={pinterestProfileListItems}
            name={PinterestSearchType.PROFILE}
            rules={{}}
            size={'medium'}
            useFormInstance={formInstance}
            label={'Which Profile’s Pins?'}
            disabled={loading || pinterestProfiles.length < 1}
            itemTemplate={profileItemTemplate}
            chooseLabel={pinterestProfiles.length > 0 ? 'Choose a Profile' : undefined}
            hideDefaultSelectOption={true}
            action={(value, complete) => {
              complete(true);
              if (value && typeof value === 'string') {
                submitFormHandler(PinterestSearchType.PROFILE, value);
              }
            }}
          />
        </div>
      )}
      {typeValue === PinterestSearchType.BOARD && (
        <div className={profileBoardsWrapperClasses}>
          <Select
            items={pinterestProfileListItems}
            name={PinterestTypesEnum.PROFILE_BOARD}
            rules={{}}
            size={'medium'}
            useFormInstance={formInstance}
            label={'Which Profile’s Boards?'}
            chooseLabel={pinterestProfileListItems.length > 0 ? 'Choose a Profile' : undefined}
            hideDefaultSelectOption={true}
            itemTemplate={profileItemTemplate}
            disabled={loading || pinterestProfiles.length < 1}
            action={(value, complete) => {
              complete(true);
              if (value && typeof value === 'string') {
                submitFormHandler(PinterestTypesEnum.PROFILE_BOARD, value);
              }
            }}
          />
        </div>
      )}
      {typeValue === PinterestSearchType.BOARD && watchProfileBoard && (
        <div className={boardsWrapperClasses}>
          <SelectAutoComplete
            name={'boardsAutoComplete'}
            label={'Choose a Board?'}
            placeholder={currentBoards.length > 0 ? 'Enter keyword or board name' : 'No Boards'}
            items={pinterestBoardsSelectItems}
            formInstance={formInstance}
            size={'medium'}
            dropdownTemplate={dropdownTemplate}
            callBackHandler={(value: string) => {
              setFilter(value);
            }}
            disabled={loading || currentBoards.length === 0}
            side={'right'}
            resetCallBackHandler={resetCallBackHandler}
          />
        </div>
      )}
      <ContentFilterSubFilterWrapper
        showFilters={
          showFilters && ((searchStep === SearchStepEnum.ONE && hasPosts) || searchStep > SearchStepEnum.ONE)
        }>
        <PinterestFilterBar
          filterSearchHandler={filterSearchHandler}
          formInstance={formInstance}
          width={width}
          isBoardFilter={typeValue === 'BOARD'}
        />
      </ContentFilterSubFilterWrapper>
      {/* Only show if we have posts from the 1 step */}
      {((showFilters && !error) || (searchStep === SearchStepEnum.TWO && !error)) && (
        <SourceLevelAction
          formInstance={formInstance}
          setShowSaveSourceModal={setShowSaveSourceModal}
          showSaveSourceModal={showSaveSourceModal}
          getArgsHandler={getPinterestInputArgs}
        />
      )}
      {error && !loading && (
        <div className={'cell'}>
          <SearchErrorBox apolloErrorKey={apolloErrorKey} filterSearchHandler={filterSearchHandler} />
        </div>
      )}
    </>
  );
};
